import { makeAutoObservable } from 'mobx';
import { Axios } from '../utilities/network';
import RootStore from './root-store';
import { VmListT, NotificationGroup, NotificationInfoBase, NotificationGroupMap, NotificationStatus, NotificationInfoResponse, NotificationAttachment, NotificationType } from '../constants/options';
import moment from 'moment';
import { isDevelopment } from '../constants/settings';
import { FailedEventStatsModel, FailedRecipientLogModel, GmassFailType, NotificationTemplateCategory, NotificationTemplateVariable } from '../constants/notifications';

export default class NotificationStore {
  rootStore: RootStore;
  loading: boolean = false;
  loadingNotification: boolean = false;

  loadingAttachment: boolean = false;

  searchingNotificationFailedRecipients: boolean = false;
  loadingFailedEventStats: boolean = false;

  failedEventStats: FailedEventStatsModel | null = null;

  downloadingNotificationAttachment: boolean = false;

  searchingContactGroup: boolean = false;
  contactGroupListCached: VmListT<NotificationGroup> = { totalCount: 0, items: [] };

  loadingGmassSenderOptions: boolean = false;

  assigningContactToGroup: boolean = false;

  loadingNotificationGroup: boolean = false;

  searchingNotificationList: boolean = false;
  notificationListCached: VmListT<NotificationInfoBase> = { totalCount: 0, items: [] };

  loadingNotificationTemplateList: boolean = false;
  loadingNotificationTemplateInfo: boolean = false;
  // notificationTemplateListCached: VmListT<NotificationInfoBase> = { totalCount: 0, items: [] };

  loadingNotificationTemplateVariables: boolean = false;
  cachedNotificationTemplateVariables: NotificationTemplateVariable[] = [];

  uploadedNotificationHashCache = new Map<string, string>();

  currentNotificationUnreachableIds: number[] = [];
  currentNotificationInfo: NotificationInfoResponse | null = null;

  openAddedRecipientsModal: boolean = false;
  filterContactMethod?: 'EMAIL' | 'APP' | 'SMS' | 'NONE';
  filterContactMode?: "YES" | "NO";
  triggerNotificationContactSearch: boolean = false;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.rootStore = rootStore;
  }

  /* #region CRUD Notification Group */

  searchNotificationGroup = async (skip: number, take: number, reqModal: {
    searchTerm?: string,
    isDeleted?: boolean,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string
  }) => {
    this.searchingContactGroup = true;
    try {
      const res = await Axios.post(`/api/Notification/Group/Search/${skip}/${take}`, reqModal);
      if (Object.keys(reqModal).length === 0) this.contactGroupListCached = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingContactGroup = false;
    }
  };

  createNotificationGroup = async (reqModal: {
    name: string,
    description: string,
    contactIds: number[],
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Notification/Group`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  updateNotificationGroup = async (reqModal: {
    id: number,
    name: string,
    description: string,
    contactIds: number[],
  }) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Notification/Group`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  deleteNotificationGroup = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Notification/Group/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }
  /* #endregion */

  /* #region CRUD Notification Group Map */

  searchNotificationGroupMap = async (skip: number, take: number, reqModal: {
    groupId?: number,
    contactId?: number,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Notification/Group/Map/Search/${skip}/${take}`, reqModal);
      return Promise.resolve(res.data as VmListT<NotificationGroupMap>);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  assignContactToNotificationGroup = async (reqModal: {
    contactIds: number[],
    groupIds: number[]
  }) => {
    this.assigningContactToGroup = true;
    try {
      const res = await Axios.post(`/api/Notification/Group/Map/Assign`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.assigningContactToGroup = false;
    }
  }

  getNotificationGroup = async (id: number) => {
    this.loadingNotificationGroup = true;
    try {
      const res = await Axios.get(`/api/Notification/Group/${id}`);
      return Promise.resolve(res.data as NotificationGroup);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationGroup = false;
    }
  }
  /* #endregion */

  /* #region CRUD Notifications */

  searchNotificationList = async (skip: number, take: number, reqModal: {
    contactId?: number,
    categoryId?: number,
    notificationStatusId?: NotificationStatus,
    methodId?: number,
    subject?: string,
    content?: string,
    plainContent?: string,
    isSuccessSent?: boolean,
    changeLogId?: number,
    createdBy?: number,
    createdAtFrom?: string,
    newsId?: number,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    scheduleAtFrom?: string,
    scheduleAtTo?: string,
    isSystem?: boolean
  }) => {
    this.searchingNotificationList = true;
    try {
      const res = await Axios.post(`/api/Notification/Search/${skip}/${take}`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingNotificationList = false;
    }
  }

  getNotification = async (id: number, triggerLoading: boolean = true) => {
    if (triggerLoading) this.loadingNotification = true;
    try {
      const res = await Axios.get(`/api/Notification/${id}`);
      return Promise.resolve(res.data as NotificationInfoResponse);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotification = false;
    }
  }

  createNotification = async (reqModal: {
    categoryId: number,
    notificationStatusId: number,
    methodId: number,
    subject: string,
    senderEmail?: string,
    content: string,
    sentToIds: string,
    sentByFilter: string,   // MIGHT BE USED TO STORE WHICH FILTERS WERE USED (JSON)
    // groupId: number,  // MIGHT HAVE MULTIPLE GROUPS, SO CAN'T BE STORED HERE
    // sentCc: string,   // NOT USED
    // sentBcc: string,  // NOT USED
    scheduleAt: string
  }) => {
    this.loadingNotification = true;
    try {
      const res = await Axios.post(`/api/Notification`, reqModal);
      return Promise.resolve(res.data as NotificationInfoResponse);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotification = false;
    }
  }

  updateNotification = async (reqModal: {
    categoryId: number,
    notificationStatusId: number,
    // groupId: number,
    methodId: number,
    subject: string,
    senderEmail?: string,
    content: string,
    sentToIds: string,
    // sentCc: string,
    // sentBcc: string,
    // sentByFilter: string,
    scheduleAt: string,
    id: number
  }) => {
    this.loadingNotification = true;
    try {
      const res = await Axios.put(`/api/Notification`, reqModal);
      return Promise.resolve(res.data as NotificationInfoResponse);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotification = false;
    }
  }

  deleteNotification = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Notification/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }
  /* #endregion */

  /* #region CRUD Notification Attachment */

  uploadNotificationAttachment = async (reqModel: {
    fileName: string,
    attachmentCategoryId: number,
    file: string,
    notificationId: number
  }) => {
    this.loadingAttachment = true;
    try {
      const res = await Axios.post(`/api/Notification/Attachment`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAttachment = false;
    }
  }

  deleteNotificationAttachment = async (id: number) => {
    this.loadingAttachment = true;
    try {
      const res = await Axios.delete(`/api/Notification/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAttachment = false;
    }
  }

  updateNotificationAttachment = async (reqModel: {
    fileName: string,
    attachmentCategoryId: number,
    originalName: string,
    id: number
  }) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Notification/Attachment`, reqModel);
      return Promise.resolve(res.data as NotificationAttachment);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  searchNotificationAttachment = async (skip: number, take: number, reqModal: {
    notificationId?: number,
    attachmentCategoryId?: number,
    searchTerm?: string
  }) => {
    this.loadingAttachment = true;
    try {
      const res = await Axios.post(`/api/Notification/Attachment/Search/${skip}/${take}`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAttachment = false;
    }
  }

  downloadNotificationAttachment = async (fileName: string) => {
    this.downloadingNotificationAttachment = true;
    try {
      const res = await Axios.get(`/api/Notification/Attachment/${fileName}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloadingNotificationAttachment = false;
    }
  }

  /* #endregion */

  searchNotificationTemplate = async (skip: number, take: number, reqModal: {
    methodId?: number,
    title?: string,
    content?: string,
    createdAtFrom?: string,
    createdAtTo?: string,
    createdBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    lastModifiedBy?: number
  }) => {
    this.loadingNotificationTemplateList = true;
    try {
      const res = await Axios.post(`/api/Notification/Template/Search/${skip}/${take}`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationTemplateList = false;
    }
  }

  getNotificationTemplate = async (id: number) => {
    this.loadingNotificationTemplateInfo = true;
    try {
      const res = await Axios.get(`/api/Notification/Template/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationTemplateInfo = false;
    }
  }

  createNotificationTemplate = async (reqModal: {
    methodId: NotificationType,
    title: string,
    content: string,
  }) => {
    this.loadingNotificationTemplateInfo = true;
    try {
      const res = await Axios.post(`/api/Notification/Template`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationTemplateInfo = false;
    }
  }

  updateNotificationTemplate = async (reqModal: {
    methodId: NotificationType,
    title: string,
    content: string,
    id: number
  }) => {
    this.loadingNotificationTemplateInfo = true;
    try {
      const res = await Axios.put(`/api/Notification/Template`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationTemplateInfo = false;
    }
  }

  removeNotificationTemplate = async (id: number) => {
    this.loadingNotificationTemplateInfo = true;
    try {
      const res = await Axios.delete(`/api/Notification/Template/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationTemplateInfo = false;
    }
  }

  searchNotificationTemplateVariables = async () => {
    this.loadingNotificationTemplateVariables = true;
    try {
      if (this.cachedNotificationTemplateVariables.length > 0) {
        return Promise.resolve({
          totalCount: this.cachedNotificationTemplateVariables.length,
          items: this.cachedNotificationTemplateVariables
        });
      }
      const res = await Axios.post(`/api/Notification/Template/Variable/Search/${0}/${0}`, {});
      this.cachedNotificationTemplateVariables = res.data.items;
      return Promise.resolve(res.data as VmListT<NotificationTemplateVariable>);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationTemplateVariables = false;
    }
  }

  uploadNotificationTemplateAttachment = async (reqModal: {
    fileName: string,
    attachmentCategoryId: number,
    file: string,
    templateId: number
  }) => {
    this.loadingAttachment = true;
    try {
      const res = await Axios.post(`/api/Notification/Template/Attachment`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAttachment = false;
    }
  }

  // Failed Email Recipients

  searchFailedEmailEvents = async (skip: number, take: number, reqModal: {
    email?: string,
    reason?: string,
    blockTypes?: GmassFailType[],
    notificationId?: number,
    contactId?: number,
    blockedAtFrom?: string,
    blockedAtTo?: string
  }) => {
    this.searchingNotificationFailedRecipients = true;
    try {
      const res = await Axios.post<VmListT<FailedRecipientLogModel>>(`/api/Notification/Failed/Emails/Search/${skip}/${take}`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingNotificationFailedRecipients = false;
    }
  }


  getFailedEventStats = async (reqModal: {
    email?: string,
    blockedAtFrom?: string,
    blockedAtTo?: string
  }, setToStore: boolean = false) => {
    this.loadingFailedEventStats = true;
    try {
      const res = await Axios.post<FailedEventStatsModel>(`/api/Notification/Failed/Emails/Stats`, reqModal);
      if (setToStore) {
        this.failedEventStats = res.data;
      }
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingFailedEventStats = false;
    }
  }

  getGmassEmailFromOptions = async () => {
    this.loadingGmassSenderOptions = true;
    try {
      const res = await Axios.get(`/api/GMass/Sender/Options`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingGmassSenderOptions = false;
    }
  };
}